import React, { useState, useEffect } from 'react';
import { Trash2, Mail, Download, Search } from 'lucide-react';
import { getNewsletterSubscribers, deleteNewsletterSubscriber } from '../../utils/api';
import { toast } from 'sonner';
import './AdminStyles.css';

const NewsletterManagement = () => {
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchSubscribers();
  }, []);

  const fetchSubscribers = async () => {
    try {
      const data = await getNewsletterSubscribers();
      setSubscribers(data);
    } catch (error) {
      toast.error('Failed to load subscribers');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (subscriberId) => {
    if (!window.confirm('Are you sure you want to remove this subscriber?')) return;

    try {
      await deleteNewsletterSubscriber(subscriberId);
      setSubscribers(subscribers.filter(s => s.id !== subscriberId));
      toast.success('Subscriber removed');
    } catch (error) {
      toast.error('Failed to remove subscriber');
    }
  };

  const exportToCSV = () => {
    const headers = ['Email', 'Subscribed At'];
    const rows = subscribers.map(s => [
      s.email,
      new Date(s.subscribed_at).toLocaleString()
    ]);
    
    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `newsletter-subscribers-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Exported to CSV');
  };

  const filteredSubscribers = subscribers.filter(s =>
    s.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="admin-page">
        <div className="loading-spinner">Loading...</div>
      </div>
    );
  }

  return (
    <div className="admin-page" data-testid="newsletter-management">
      <div className="admin-header">
        <div>
          <h1>Newsletter Subscribers</h1>
          <p className="admin-subtitle">{subscribers.length} total subscribers</p>
        </div>
        <button 
          className="btn btn-primary export-btn"
          onClick={exportToCSV}
          disabled={subscribers.length === 0}
          data-testid="export-csv-btn"
        >
          <Download size={18} />
          Export CSV
        </button>
      </div>

      <div className="search-bar">
        <Search size={18} />
        <input
          type="text"
          placeholder="Search by email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          data-testid="search-subscribers"
        />
      </div>

      {filteredSubscribers.length === 0 ? (
        <div className="empty-state">
          <Mail size={48} />
          <h3>{searchTerm ? 'No matching subscribers' : 'No subscribers yet'}</h3>
          <p>{searchTerm ? 'Try a different search term' : 'Subscribers will appear here once people sign up'}</p>
        </div>
      ) : (
        <div className="data-table">
          <table>
            <thead>
              <tr>
                <th>Email</th>
                <th>Subscribed At</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredSubscribers.map((subscriber) => (
                <tr key={subscriber.id} data-testid={`subscriber-row-${subscriber.id}`}>
                  <td>
                    <div className="email-cell">
                      <Mail size={16} />
                      <a href={`mailto:${subscriber.email}`}>{subscriber.email}</a>
                    </div>
                  </td>
                  <td>{formatDate(subscriber.subscribed_at)}</td>
                  <td>
                    <button
                      className="btn btn-danger btn-sm"
                      onClick={() => handleDelete(subscriber.id)}
                      title="Remove subscriber"
                      data-testid={`delete-subscriber-${subscriber.id}`}
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default NewsletterManagement;
